import React, { useState } from "react";
import { toast } from "sonner";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  IconButton,
  CircularProgress
} from "@material-ui/core";
import { Close as CloseIcon } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";

import toastError from "../../errors/toastError";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  helperText: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: "0.875rem"
  }
}));

const MAX_BUTTONS = 3;

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required("Nome é obrigatório")
    .matches(/^[a-z0-9_]+$/, "Nome deve conter apenas letras minúsculas, números e underscore"),
  category: Yup.string().required("Categoria é obrigatória"),
  language: Yup.string().required("Idioma é obrigatório"),
  body: Yup.string()
    .required("Corpo da mensagem é obrigatório")
    .min(1, "Corpo da mensagem não pode estar vazio"),
  buttons: Yup.array()
    .of(
      Yup.object().shape({
        type: Yup.string().required("Tipo é obrigatório"),
        text: Yup.string()
          .required("Texto é obrigatório")
          .test(
            "no-variables",
            "O texto do botão não pode conter variáveis {{}}",
            value => !value || !/{{[^}]+}}/.test(value)
          ),
        url: Yup.string().when("type", {
          is: "URL",
          then: schema => schema.required("URL é obrigatória").url("URL inválida"),
          otherwise: schema => schema.notRequired()
        }),
        phoneNumber: Yup.string().when("type", {
          is: "PHONE_NUMBER",
          then: schema => schema.required("Telefone é obrigatório"),
          otherwise: schema => schema.notRequired()
        })
      })
    )
    .max(MAX_BUTTONS, `Você pode adicionar no máximo ${MAX_BUTTONS} botões`)
});

const categoryOptions = [
  { value: "UTILITY", label: "Utilitário" },
  { value: "MARKETING", label: "Marketing" },
  { value: "AUTHENTICATION", label: "Autenticação" },
];

const languageOptions = [
  { value: "pt_BR", label: "Português (Brasil)" },
  { value: "en_US", label: "Inglês (EUA)" },
  { value: "es_ES", label: "Espanhol" },
];

const CreateNotificameTemplateModal = ({ open, onClose, whatsappId, onSuccess }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);

  const initialValues = {
    name: "",
    category: "UTILITY",
    language: "pt_BR",
    body: "",
    buttons: []
  };

  const handleSubmit = async (values, actions) => {
    try {
      setLoading(true);
      
      const requestData = {
        whatsappId: parseInt(whatsappId),
        name: values.name,
        language: values.language,
        category: values.category,
        body: values.body,
        buttons: values.buttons
      };

      const { data } = await api.post("/templates/create", requestData);
      
      toast.success("Template criado com sucesso! Aguardando aprovação do WhatsApp.");
      
      if (onSuccess) {
        onSuccess(data.template);
      }
      
      onClose();
      actions.resetForm();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h6">
          Criar Novo Template
        </Typography>
        <IconButton onClick={onClose} className={classes.closeButton}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, touched, errors, isSubmitting, setFieldValue }) => (
          <Form>
            <DialogContent dividers>
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  fullWidth
                  label="Nome do Template"
                  name="name"
                  value={values.name}
                  onChange={(e) => setFieldValue("name", e.target.value.toLowerCase())}
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                  placeholder="exemplo: template_boas_vindas_v1"
                />
                <Typography variant="caption" className={classes.helperText}>
                  O nome deve conter apenas letras minúsculas, números e underscore (_)
                </Typography>
                
                <FormControl fullWidth>
                  <InputLabel>Categoria</InputLabel>
                  <Select
                    name="category"
                    value={values.category}
                    onChange={(e) => setFieldValue("category", e.target.value)}
                    error={touched.category && Boolean(errors.category)}
                  >
                    {categoryOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <FormControl fullWidth>
                  <InputLabel>Idioma</InputLabel>
                  <Select
                    name="language"
                    value={values.language}
                    onChange={(e) => setFieldValue("language", e.target.value)}
                    error={touched.language && Boolean(errors.language)}
                  >
                    {languageOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  label="Corpo da Mensagem"
                  name="body"
                  value={values.body}
                  onChange={(e) => setFieldValue("body", e.target.value)}
                  error={touched.body && Boolean(errors.body)}
                  helperText={touched.body && errors.body}
                  placeholder="Digite o texto do template. Use {{NAME}}, {{PROTOCOL}}, {{USERNAME}} e {{CUSTOM}} para variáveis."
                />
                <Typography variant="caption" className={classes.helperText}>
                  Para usar variáveis, você pode colocar qualquer uma dessas disponíveis: {`{{NAME}}`}, {`{{PROTOCOL}}`}, {`{{USERNAME}}`}, {`{{CUSTOM}}`}
                  <br />
                  Exemplo: "Olá {`{{NAME}}`}, seu protocolo é {`{{PROTOCOL}}`}. Atendido por {`{{USERNAME}}`}."
                  <br />
                  Observação: cada ocorrência de {`{{CUSTOM}}`} é tratada como um valor diferente. No envio via API externa, os valores serão enviados como {`CUSTOM_1`}, {`CUSTOM_2`}, ... na mesma ordem.
                </Typography>
                <Box mt={3}>
                  <Typography variant="subtitle1">Botões</Typography>
                  <Typography variant="caption" className={classes.helperText}>
                    Os botões são enviados junto com o template para o WhatsApp. O texto deve ser fixo, sem variáveis.
                  </Typography>

                  {values.buttons?.map((button, index) => (
                    <Box
                      key={index}
                      display="flex"
                      alignItems="center"
                      mt={2}
                      gridGap={8}
                    >
                      <FormControl style={{ minWidth: 150 }}>
                        <InputLabel>Tipo</InputLabel>
                        <Select
                          value={button.type || ""}
                          onChange={e => {
                            const newButtons = [...values.buttons];
                            newButtons[index] = {
                              ...newButtons[index],
                              type: e.target.value
                            };
                            setFieldValue("buttons", newButtons);
                          }}
                        >
                          <MenuItem value="QUICK_REPLY">Resposta rápida</MenuItem>
                          <MenuItem value="URL">URL</MenuItem>
                          <MenuItem value="PHONE_NUMBER">Telefone</MenuItem>
                        </Select>
                      </FormControl>

                      <TextField
                        label="Texto do botão"
                        value={button.text || ""}
                        onChange={e => {
                          const newButtons = [...values.buttons];
                          newButtons[index] = {
                            ...newButtons[index],
                            text: e.target.value
                          };
                          setFieldValue("buttons", newButtons);
                        }}
                        style={{ flex: 1 }}
                      />

                      {button.type === "URL" && (
                        <TextField
                          label="URL"
                          value={button.url || ""}
                          onChange={e => {
                            const newButtons = [...values.buttons];
                            newButtons[index] = {
                              ...newButtons[index],
                              url: e.target.value
                            };
                            setFieldValue("buttons", newButtons);
                          }}
                          style={{ flex: 1 }}
                        />
                      )}

                      {button.type === "PHONE_NUMBER" && (
                        <TextField
                          label="Telefone"
                          value={button.phoneNumber || ""}
                          onChange={e => {
                            const newButtons = [...values.buttons];
                            newButtons[index] = {
                              ...newButtons[index],
                              phoneNumber: e.target.value
                            };
                            setFieldValue("buttons", newButtons);
                          }}
                          style={{ flex: 1 }}
                        />
                      )}

                      <Button
                        color="secondary"
                        onClick={() => {
                          const newButtons = values.buttons.filter((_, i) => i !== index);
                          setFieldValue("buttons", newButtons);
                        }}
                      >
                        Remover
                      </Button>
                    </Box>
                  ))}

                  {touched.buttons && typeof errors.buttons === "string" && (
                    <Typography color="error" variant="caption">
                      {errors.buttons}
                    </Typography>
                  )}

                  <Box mt={2}>
                    <Button
                      variant="outlined"
                      color="primary"
                      disabled={values.buttons.length >= MAX_BUTTONS}
                      onClick={() => {
                        if (values.buttons.length >= MAX_BUTTONS) return;
                        setFieldValue("buttons", [
                          ...values.buttons,
                          { type: "QUICK_REPLY", text: "" }
                        ]);
                      }}
                    >
                      Adicionar botão
                    </Button>
                  </Box>
                </Box>
              </Box>
            </DialogContent>
            
            <DialogActions>
              <Button 
                onClick={onClose} 
                color="secondary"
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting || loading}
                startIcon={loading && <CircularProgress size={20} />}
              >
                {loading ? "Criando..." : "Criar Template"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default CreateNotificameTemplateModal;

